import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type NeedDto,
  type TextDocumentDto,
  useCreateNeedTextDocumentMutation,
  useListTextDocumentsForNeedQuery,
} from "../../../autogen/bff-api";
import { CombinedDocuments } from "../../../common/documents/CombinedDocuments";
import { isIncompleteFileUpload, type IncompleteFileUpload } from "../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../common/documents/UppyUploader/useUppyUploader";

interface Props {
  need: NeedDto;
  refetch: () => void;
  canEdit: boolean;
}

export const NeedDocuments = ({ need, refetch, canEdit }: Props) => {
  const { t } = useTranslation();
  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityId: need.id,
    entityType: "Need",
    reloadEntity: refetch,
  });

  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateNeedTextDocumentMutation();
  const { data: textDocumentRes, isLoading: isLoadingTextDocuments } = useListTextDocumentsForNeedQuery({
    needId: need.id,
  });
  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);

  useEffect(() => {
    setDocuments(need.documents ?? []);
  }, [need.documents, setDocuments]);

  useEffect(() => {
    if (textDocumentRes) setTextDocuments(textDocumentRes.documents);
  }, [textDocumentRes]);

  const createTextDocumentForNeed = useCallback(
    () =>
      createTextDocument({
        createNeedTextDocumentRequest: { needId: need.id, name: "", text: "", tags: [] },
      }).unwrap(),
    [createTextDocument, need.id]
  );

  return (
    <CombinedDocuments
      uploadedDocuments={documents}
      setUploadedDocuments={setDocuments}
      textDocuments={textDocuments}
      setTextDocuments={setTextDocuments}
      createTextDocument={canEdit ? createTextDocumentForNeed : undefined}
      isCreatingTextDocument={isCreatingTextDocument}
      canEditTextDocuments={canEdit}
      canEditDocumentNames={canEdit}
      canDeleteDocuments={canEdit}
      isLoading={isLoadingTextDocuments}
      documentUploadConfig={
        canEdit
          ? {
              uppy,
              dropTargetId: "needFileTarget",
              dropTargetHelperText: t("Upload relevant documents"),
              uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
              removeUpload,
            }
          : undefined
      }
      isTagsColumnVisible={false}
    />
  );
};
